'use client'
import type { ChainId } from '@morpho-org/blue-sdk'

import { parseApiAsset } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { Icon } from '@repo/ui'
import { Timelock } from '@repo/ui/icons'

import { scrollToElementAfterDelay } from '@/helpers/scrollToElement'

import type { PendingTimelocks } from './toPendingTimelocks'

import {
  formatPendingActionsLabel,
  PendingActionsList,
  VaultPendingTimelockInfo
} from '../../components/VaultPendingTimelockInfo'
import { getTimelockFunctionConfig as getV1TimelockFunctionConfig } from '../../VaultV1/VaultPendingTimelock/utils'
import { VaultV2PendingTimelock } from '../VaultV2PendingTimelock'
import UnderlyingVaultPendingTimelockItem from '../VaultV2PendingTimelock/UnderlyingVaultPendingTimelockItem'
import { getTimelockCriticality } from '../VaultV2PendingTimelock/utils'

interface VaultHeroPendingActionsProps {
  chainId: ChainId
  pendingTimelocks: PendingTimelocks
}

const VaultV2HeroPendingActions = ({
  chainId,
  pendingTimelocks: data
}: VaultHeroPendingActionsProps) => {
  const {
    pendingTimelocks,
    underlyingVaultItems,
    caps,
    vaultAsset,
    timelocks,
    currentPerformanceFee,
    currentManagementFee,
    currentManagementFeeRecipient,
    currentPerformanceFeeRecipient
  } = data
  const vaultSupplyToken = parseApiAsset(vaultAsset) ?? null

  const v2Items = pendingTimelocks
  const v1Items = underlyingVaultItems
  const totalCount = v2Items.length + v1Items.length

  const handleV1Click = () => {
    scrollToElementAfterDelay('exposure-section')
  }

  if (totalCount === 0) {
    return null
  }

  const hasV2Critical = v2Items.some(item => getTimelockCriticality(item.functionName) === 'high')

  const hasV1Critical = v1Items.some(entry => {
    const { pendingTimelock } = entry
    const hasPreviousValue =
      pendingTimelock.decodedData?.__typename === 'VaultSetCapPendingData' &&
      pendingTimelock.decodedData.market
        ? isDefined(entry.marketSupplyCaps[pendingTimelock.decodedData.market.marketId])
        : undefined

    return (
      getV1TimelockFunctionConfig({
        functionName: pendingTimelock.functionName,
        hasPreviousValue
      })?.criticality === 'high'
    )
  })

  const hasCriticalAction = hasV2Critical || hasV1Critical

  const pendingActionsText = formatPendingActionsLabel(totalCount)

  const pendingActionsContent = (
    <PendingActionsList count={totalCount} data-testid="vault-hero-pending-actions-content">
      {v2Items.map((item, index) => (
        <VaultV2PendingTimelock
          key={`v2-${item.functionName}-${index}`}
          validAt={item.validAt}
          functionName={item.functionName}
          decodedData={item.decodedData}
          caps={caps}
          vaultSupplyToken={vaultSupplyToken}
          timelocks={timelocks}
          currentPerformanceFee={currentPerformanceFee}
          currentManagementFee={currentManagementFee}
          currentManagementFeeRecipient={currentManagementFeeRecipient}
          currentPerformanceFeeRecipient={currentPerformanceFeeRecipient}
        />
      ))}
      {v1Items.map((item, index) => (
        <UnderlyingVaultPendingTimelockItem
          key={`v1-${item.pendingTimelock.functionName}-${index}`}
          chainId={chainId}
          pendingTimelock={item.pendingTimelock}
          marketSupplyCaps={item.marketSupplyCaps}
          vault={item.vault}
          selectable
          onClick={handleV1Click}
        />
      ))}
    </PendingActionsList>
  )

  return (
    <VaultPendingTimelockInfo content={pendingActionsContent}>
      <div
        className="gap-sm flex flex-row items-center"
        data-testid="vault-hero-pending-actions-trigger"
      >
        <div
          className={cn(
            'flex h-[26px] cursor-default items-center justify-center gap-0.5 rounded-full pr-[10px] pl-[5px]',
            hasCriticalAction
              ? 'bg-destructive-foreground/20 hover:bg-destructive-foreground/25 text-destructive-foreground hover:text-destructive-foreground active:text-destructive-foreground disabled:text-destructive-foreground/35'
              : 'bg-primary/15 hover:bg-primary/20'
          )}
        >
          <Icon
            icon={<Timelock />}
            className={cn(
              'size-5',
              hasCriticalAction ? 'text-destructive-foreground' : 'text-info-foreground'
            )}
          />
          <span
            className={cn(
              'text-3xs',
              hasCriticalAction ? 'text-destructive-foreground' : 'text-info-foreground'
            )}
            style={{ lineHeight: 'normal', whiteSpace: 'nowrap' }}
          >
            {pendingActionsText}
          </span>
        </div>
      </div>
    </VaultPendingTimelockInfo>
  )
}

export default VaultV2HeroPendingActions
